import { useEffect, useRef, useState, FormEvent } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth, type AuthModalView as View } from "@/contexts/AuthContext";

const MIN_PASSWORD_LENGTH = 6;

const GoogleIcon = () => (
  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const GoogleButton = ({ onClick, disabled }: { onClick: () => void; disabled: boolean }) => (
  <Button type="button" variant="outline" className="w-full" onClick={onClick} disabled={disabled}>
    <GoogleIcon />
    Continue with Google
  </Button>
);

const Divider = () => (
  <div className="relative my-4">
    <div className="absolute inset-0 flex items-center">
      <span className="w-full border-t border-border" />
    </div>
    <div className="relative flex justify-center text-xs uppercase">
      <span className="bg-background px-2 text-muted-foreground">or</span>
    </div>
  </div>
);

const ErrorText = ({ children }: { children: React.ReactNode }) => (
  <p role="alert" className="text-sm text-destructive">
    {children}
  </p>
);

/** Supabase's raw messages don't explain the common Google-account case. */
function friendlySignInError(err: unknown): string {
  const message = err instanceof Error ? err.message : "Sign in failed";
  if (/invalid login credentials/i.test(message)) {
    return "Wrong email or password. If you've signed in with Google before, use the Google button — or \"Forgot password?\" to set a password.";
  }
  if (/email not confirmed/i.test(message)) {
    return "Please confirm your email first — check your inbox for the confirmation link.";
  }
  return message;
}

/**
 * Global sign-in dialog: Google OAuth + email/password (sign in, create
 * account, forgot password). Open it via `setAuthModalOpen(true)` from useAuth.
 */
export const AuthModal = () => {
  const {
    user,
    authModalOpen: open,
    authModalView,
    setAuthModalOpen,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    resetPassword,
  } = useAuth();

  const [view, setView] = useState<View>("auth");
  const [tab, setTab] = useState("sign-in");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ title: string; body: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  // Bumped on close so requests still in flight from a dismissed attempt
  // don't write their result into the next opening of the dialog.
  const attemptRef = useRef(0);
  const startAttempt = () => {
    const attempt = attemptRef.current;
    return () => attempt !== attemptRef.current;
  };

  const resetState = () => {
    attemptRef.current += 1;
    setView("auth");
    setTab("sign-in");
    setError(null);
    setSuccess(null);
    setLoading(false);
    setEmail("");
    setPassword("");
    setConfirm("");
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetState();
    setAuthModalOpen(next);
  };

  // Start on the screen the opener asked for (e.g. "forgot" from /reset-password).
  useEffect(() => {
    if (open) setView(authModalView);
  }, [open, authModalView]);

  // Close once a session appears (password sign-in, or sign-in in another tab).
  useEffect(() => {
    if (user && open) handleOpenChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, open]);

  const switchTab = (next: string) => {
    setTab(next);
    setError(null);
    setPassword("");
    setConfirm("");
  };

  const handleSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const stale = startAttempt();
    try {
      await signInWithEmail(email, password);
      // The effect above closes the dialog when the session lands.
    } catch (err: unknown) {
      if (!stale()) setError(friendlySignInError(err));
    } finally {
      if (!stale()) setLoading(false);
    }
  };

  const handleSignUp = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    const stale = startAttempt();
    try {
      const result = await signUpWithEmail(email, password);
      // Same message whether or not the email is already registered, so the
      // form can't be used to find out who has an account.
      if (result === "confirm" && !stale()) {
        setSuccess({
          title: "Check your email",
          body: `If ${email.trim()} is new to Quizrun, we've sent a confirmation link — click it to finish creating your account. No email after a few minutes? You may already have an account: sign in (or continue with Google), or use "Forgot password?".`,
        });
      }
    } catch (err: unknown) {
      if (!stale()) setError(err instanceof Error ? err.message : "Sign up failed");
    } finally {
      if (!stale()) setLoading(false);
    }
  };

  const handleForgotPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const stale = startAttempt();
    try {
      await resetPassword(email);
      if (!stale()) {
        setSuccess({
          title: "Check your email",
          body: `If an account exists for ${email.trim()}, we've sent a password reset link.`,
        });
      }
    } catch (err: unknown) {
      if (!stale()) setError(err instanceof Error ? err.message : "Failed to send reset link");
    } finally {
      if (!stale()) setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    const stale = startAttempt();
    try {
      await signInWithGoogle();
    } catch (err: unknown) {
      if (stale()) return;
      setError(err instanceof Error ? err.message : "Google sign in failed");
      setLoading(false);
    }
  };
  const emailField = (id: string, autoFocus = false) => (
    <div className="space-y-2">
      <Label htmlFor={id}>Email</Label>
      <Input
        id={id}
        type="email"
        autoComplete="email"
        placeholder="you@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        autoFocus={autoFocus}
      />
    </div>
  );

  let body: React.ReactNode;
  if (success) {
    body = (
      <>
        <DialogHeader>
          <DialogTitle>{success.title}</DialogTitle>
          <DialogDescription>{success.body}</DialogDescription>
        </DialogHeader>
        <Button className="w-full mt-2" onClick={() => handleOpenChange(false)}>
          OK
        </Button>
      </>
    );
  } else if (view === "forgot") {
    body = (
      <>
        <DialogHeader>
          <DialogTitle>Reset password</DialogTitle>
          <DialogDescription>
            Enter your email and we'll send you a link to set a new password.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleForgotPassword} className="space-y-4">
          {emailField("forgot-email", true)}
          {error && <ErrorText>{error}</ErrorText>}
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Sending…" : "Send reset link"}
          </Button>
          <button
            type="button"
            className="w-full text-sm text-muted-foreground hover:text-foreground text-center"
            onClick={() => {
              setError(null);
              setView("auth");
            }}
          >
            ← Back to sign in
          </button>
        </form>
      </>
    );
  } else {
    body = (
      <>
        <DialogHeader>
          <DialogTitle>Welcome</DialogTitle>
          <DialogDescription>
            Sign in to your account or create a new one.
          </DialogDescription>
        </DialogHeader>

        <GoogleButton onClick={handleGoogleSignIn} disabled={loading} />
        <Divider />

        <Tabs value={tab} onValueChange={switchTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="sign-in">Sign in</TabsTrigger>
            <TabsTrigger value="sign-up">Create account</TabsTrigger>
          </TabsList>

          <TabsContent value="sign-in">
            <form onSubmit={handleSignIn} className="space-y-4">
              {emailField("si-email")}
              <div className="space-y-2">
                <Label htmlFor="si-password">Password</Label>
                <Input
                  id="si-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {error && <ErrorText>{error}</ErrorText>}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Signing in…" : "Sign in with email"}
              </Button>
              <button
                type="button"
                className="w-full text-sm text-muted-foreground hover:text-foreground text-center"
                onClick={() => {
                  setError(null);
                  setView("forgot");
                }}
              >
                Forgot password?
              </button>
            </form>
          </TabsContent>

          <TabsContent value="sign-up">
            <form onSubmit={handleSignUp} className="space-y-4">
              {emailField("su-email")}
              <div className="space-y-2">
                <Label htmlFor="su-password">Password</Label>
                <Input
                  id="su-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="su-confirm">Confirm password</Label>
                <Input
                  id="su-confirm"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                />
              </div>
              {error && <ErrorText>{error}</ErrorText>}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Creating account…" : "Create account with email"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">{body}</DialogContent>
    </Dialog>
  );
};
